import { StaticRouter } from 'react-router-dom/server'
import { useReadStoryAccess } from '@opallabs/api'
import { type SimpleWorkspace } from '@opallabs/concepts'
import { type Theme } from '@opallabs/gem'
import { DefaultQueryClientProvider } from '@opallabs/higgs'
import { ThemeProvider, WorkspaceContext } from '@opallabs/hooks'
import { ensureOwnerIsInAccess } from '../helpers'
import { PermissionsSummary } from '../PermissionSummary'
import { useComputedAccess } from '../useComputedAccessData'

type StoryPermissionsSummaryProps = {
  storyUuid: string
  ownerUuid?: string
  onOpenShareModal?: () => void
}

/**
 * Self-fetching permissions summary for stories. Fetches access metadata
 * (including effective privacy) internally and resolves the owner User
 * object via the API. The caller only needs to provide the owner's UUID.
 *
 * Requires `DefaultQueryClientProvider` and `WorkspaceContext` in the tree.
 */
export const StoryPermissionsSummary: React.FC<
  StoryPermissionsSummaryProps
> = ({ storyUuid, ownerUuid, onOpenShareModal }) => {
  const rawMetadataQuery = useReadStoryAccess({ storyUuid, enabled: true })
  const metadataQuery = ensureOwnerIsInAccess(rawMetadataQuery, ownerUuid)

  const { owner } = useComputedAccess({
    accessMetadata: metadataQuery.data,
    ownerId: ownerUuid,
  })

  return (
    <PermissionsSummary
      hideUserAvatar={!owner}
      metadataQuery={metadataQuery}
      resourceName="story"
      resourceOwner={owner}
      onOpenShareModal={onOpenShareModal}
    />
  )
}

/**
 * Queryable version of `StoryPermissionsSummary` that brings its own
 * providers. Use this when rendering from CoffeeScript or outside a
 * React Query / workspace context tree.
 */
export const QueryableStoryPermissionsSummary: React.FC<
  StoryPermissionsSummaryProps & {
    workspace: SimpleWorkspace
    theme?: Theme
  }
> = ({ workspace, theme = 'light', ...props }) => {
  const location = window.top?.location ?? window.location
  return (
    <DefaultQueryClientProvider>
      <StaticRouter location={location.pathname}>
        <WorkspaceContext.Provider value={workspace}>
          <ThemeProvider theme={theme}>
            <StoryPermissionsSummary {...props} />
          </ThemeProvider>
        </WorkspaceContext.Provider>
      </StaticRouter>
    </DefaultQueryClientProvider>
  )
}
